<template>
    <span
        v-bind:class="badgeClasses"
        v-bind:style="badgeStyles"
        v-on:click="handleClick"
    >
        <div v-if="$slots.leftIcon" class="badge__icon badge__icon--left">
            <slot
                v-if="$slots.leftIcon"
                name="leftIcon"
            />
        </div>
        <SboIcon
            v-if="leftIconName"
            v-bind:name="leftIconName"
            v-bind:size="16"
            class="badge__icon badge__icon--left"
        />

        <slot>{{ text }}</slot>

        <div v-if="$slots.rightIcon" class="badge__icon badge__icon--right">
            <slot
                v-if="$slots.rightIcon"
                name="rightIcon"
            />
        </div>
        <SboIcon
            v-if="iconName"
            v-bind:name="iconName"
            v-bind:size="16"
            class="badge__icon badge__icon--right"
        />
    </span>
</template>

<script lang="ts" setup>
    import type { IconName } from '@sbo/icons-vue';
    import { computed } from 'vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import type { BadgeColor, BadgeSize, BadgeVariant } from '@/interface/IBadge';

    interface Props {
        text?: string;
        variant?: BadgeVariant;
        color?: BadgeColor;
        size?: BadgeSize;
        padding?: string;
        iconName?: IconName;
        leftIconName?: IconName;
        clickable?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        text: '',
        variant: 'light',
        color: 'gray',
        size: 'sm',
        padding: undefined,
        iconName: undefined,
        leftIconName: undefined,
        clickable: false,
    });

    const emit = defineEmits<{
        click: [event: MouseEvent];
    }>();

    const badgeClasses = computed(() => [
        'badge',
        `badge--${props.variant}-${props.color}`,
        `badge--size-${props.size}`,
        { 'badge--clickable': props.clickable },
    ]);

    const badgeStyles = computed(() => ({
        cursor: props.clickable ? 'pointer' : 'default',
        ...(props.padding && { padding: props.padding }),
    }));

    const handleClick = (event: MouseEvent) => {
        if (props.clickable) {
            emit('click', event);
        }
    };
</script>

<style lang="scss" scoped>
@import "@/components/common/badge/Badge";
</style>
